import { DoublyLinkedList } from './doubly-linked-list.js'

/**
 * Cola (Queue) — estructura FIFO (First In, First Out).
 * El primer elemento en entrar es el primero en salir.
 * Acepta cualquier valor: primitivos u objetos.
 * Para colas con prioridad, usar PriorityQueue.
 * @template T - El tipo de los valores almacenados en la cola.
 */
export class Queue<T> {
  readonly #list = new DoublyLinkedList<T>()

  /**
   * Agrega un valor al final de la cola.
   * @param value - El valor a agregar (primitivo u objeto).
   */
  enqueue(value: T): void {
    this.#list.push(value)
  }

  /**
   * Elimina y retorna el valor del frente de la cola.
   * @returns El valor eliminado, o undefined si la cola está vacía.
   */
  dequeue(): T | undefined {
    return this.#list.shift()
  }

  /**
   * Retorna el valor del frente sin eliminarlo.
   * @returns El valor del frente, o undefined si la cola está vacía.
   */
  peek(): T | undefined {
    return this.#list.head?.value
  }

  /**
   * Cancela (elimina) el primer elemento que satisface el predicado.
   * Para primitivos: cancel(v => v === "T-001")
   * Para objetos:   cancel(v => v.id === "T-001")
   * @param predicate - Función que recibe el valor y retorna boolean.
   * @returns true si se canceló, false si no se encontró.
   */
  cancel(predicate: (value: T) => boolean): boolean {
    const node = this.#list.findNode(predicate)
    if (!node) return false
    this.#list.removeNode(node)
    return true
  }

  /**
   * Retorna la posición (base 1) del primer elemento que satisface el predicado.
   * Para primitivos: positionOf(v => v === "T-001")
   * Para objetos:   positionOf(v => v.id === "T-001")
   * @param predicate - Función que recibe el valor y retorna boolean.
   * @returns La posición en la cola, o undefined si no se encontró.
   */
  positionOf(predicate: (value: T) => boolean): number | undefined {
    let current = this.#list.head
    let position = 1
    while (current) {
      if (predicate(current.value)) return position
      current = current.next
      position++
    }
    return undefined
  }

  /**
   * Retorna un array con todos los valores en espera, sin modificar la cola.
   */
  toArray(): T[] {
    return this.#list.toArray()
  }

  /**
   * Vacía la cola completa.
   */
  clear(): void {
    this.#list.clear()
  }

  /**
   * Indica si la cola está vacía.
   */
  isEmpty(): boolean {
    return this.#list.isEmpty()
  }

  /**
   * Cantidad de elementos en la cola.
   */
  get size(): number {
    return this.#list.size
  }

  /**
   * Hace la cola iterable con for...of, spread y destructuring.
   */
  *[Symbol.iterator](): Iterator<T> {
    yield* this.#list
  }
}